import { useEffect } from "react";

/** Lightweight SEO hook — sets document.title + meta tags without a full react-helmet dep.
 *  Also injects a single JSON-LD script tag when a `jsonLd` object is provided.
 *  Call once inside your page component. Values reset on unmount so nav feels correct.
 */
export function useSeo({ title, description, canonical, image, ogType = "website", jsonLd }) {
  useEffect(() => {
    const prev = { title: document.title };

    if (title) document.title = title;

    const setMeta = (attr, key, value) => {
      if (!value) return null;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prevContent = el.getAttribute("content");
      el.setAttribute("content", value);
      return () => {
        if (prevContent === null) el.remove();
        else el.setAttribute("content", prevContent);
      };
    };

    const cleanups = [
      setMeta("name", "description", description),
      setMeta("property", "og:title", title),
      setMeta("property", "og:description", description),
      setMeta("property", "og:type", ogType),
      setMeta("property", "og:image", image),
      setMeta("property", "og:url", canonical || (typeof window !== "undefined" && window.location.href)),
      setMeta("name", "twitter:card", image ? "summary_large_image" : "summary"),
      setMeta("name", "twitter:title", title),
      setMeta("name", "twitter:description", description),
      setMeta("name", "twitter:image", image),
    ].filter(Boolean);

    // Canonical link
    let canonicalEl = document.head.querySelector('link[rel="canonical"]');
    const hadCanonical = !!canonicalEl;
    const prevHref = canonicalEl?.getAttribute("href");
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    // JSON-LD structured data
    let ldEl = null;
    if (jsonLd) {
      ldEl = document.createElement("script");
      ldEl.type = "application/ld+json";
      ldEl.text = JSON.stringify(jsonLd);
      ldEl.setAttribute("data-seo", "1");
      document.head.appendChild(ldEl);
    }

    return () => {
      document.title = prev.title;
      cleanups.forEach((fn) => fn && fn());
      if (canonical) {
        if (!hadCanonical) canonicalEl?.remove();
        else if (prevHref) canonicalEl?.setAttribute("href", prevHref);
      }
      ldEl?.remove();
    };
  }, [title, description, canonical, image, ogType, JSON.stringify(jsonLd)]); // eslint-disable-line
}
